import { useState, useEffect } from "react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Languages, Loader2, Check, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const SECTIONS = [
  { key: "venues", label: "Venues" },
  { key: "menu_items", label: "Menu Items" },
  { key: "promotions", label: "Promotions" },
  { key: "taxonomy", label: "Taxonomy" },
  { key: "cms_pages", label: "CMS Pages" },
];

export default function AdminTranslations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("venues");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState({});
  const [translating, setTranslating] = useState({});
  const [edits, setEdits] = useState({});
  const [bulkRunning, setBulkRunning] = useState(null); // section key or "all"

  useEffect(() => {
    axios.get(`${API}/admin/translations`)
      .then(res => setData(res.data))
      .catch(() => toast.error("Failed to load translations"))
      .finally(() => setLoading(false));
  }, []);

  const reloadData = async () => {
    try {
      const res = await axios.get(`${API}/admin/translations`);
      setData(res.data);
    } catch {}
  };

  const handleBulkTranslate = async (section) => {
    const label = section === "all" ? "all sections" : SECTIONS.find(s => s.key === section)?.label || section;
    const missing = section === "all"
      ? SECTIONS.reduce((sum, s) => sum + getMissingCount(s.key), 0)
      : getMissingCount(section);
    if (missing === 0) {
      toast.info(`No missing EL fields in ${label}`);
      return;
    }
    if (!window.confirm(`Auto-translate ~${missing} missing EL field(s) in ${label}? This uses LLM credits.`)) return;
    setBulkRunning(section);
    const toastId = toast.loading(`Translating ${label}... this may take a minute`);
    try {
      const res = await axios.post(`${API}/admin/translations/bulk-translate`, { section });
      const { translated, skipped, failed } = res.data;
      toast.success(`Done: ${translated} translated, ${skipped} skipped, ${failed} failed`, { id: toastId });
      await reloadData();
      setEdits({});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk translate failed", { id: toastId });
    } finally {
      setBulkRunning(null);
    }
  };

  const items = data?.[activeSection] || [];
  const filtered = items.filter(item =>
    !search || item.name?.toLowerCase().includes(search.toLowerCase())
  );

  const getMissingCount = (section) => {
    const sectionItems = data?.[section] || [];
    return sectionItems.filter(item => {
      const fields = item.fields || {};
      return Object.values(fields).some(f => f.en && !f.el);
    }).length;
  };

  const handleEdit = (itemId, field, value) => {
    setEdits(prev => ({ ...prev, [`${itemId}_${field}`]: value }));
  };

  const handleSave = async (item, field) => {
    const key = `${item.id}_${field}`;
    const value = edits[key];
    if (value === undefined) return;
    
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const collection = item.collection || activeSection;
      await axios.put(`${API}/admin/translations/${collection}/${item.id}`, { [`${field}_el`]: value });
      setData(prev => {
        const updated = { ...prev };
        const section = updated[activeSection].map(i => {
          if (i.id === item.id) {
            return { ...i, fields: { ...i.fields, [field]: { ...i.fields[field], el: value } } };
          }
          return i;
        });
        updated[activeSection] = section;
        return updated;
      });
      toast.success("Saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(prev => ({ ...prev, [key]: false })); }
  };

  const handleAutoTranslate = async (item, field) => {
    const enText = item.fields?.[field]?.en;
    if (!enText) return;
    const key = `${item.id}_${field}`;
    setTranslating(prev => ({ ...prev, [key]: true }));
    try {
      const res = await axios.post(`${API}/admin/translate`, { text: enText, source_lang: "en", target_lang: "el" });
      if (res.data.translated) {
        setEdits(prev => ({ ...prev, [key]: res.data.translated }));
      }
    } catch { toast.error("Translation failed"); }
    finally { setTranslating(prev => ({ ...prev, [key]: false })); }
  };

  if (loading) {
    return <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="animate-pulse bg-muted h-12 rounded-lg" />)}</div>;
  }

  return (
    <div data-testid="admin-translations-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
            <Languages className="h-6 w-6 text-primary" /> Translations Manager
          </h1>
          <p className="text-sm text-muted-foreground mt-1">View and manage all EN/EL content translations</p>
        </div>
        <Button
          onClick={() => handleBulkTranslate("all")}
          disabled={bulkRunning !== null}
          className="gap-2"
          data-testid="bulk-translate-all-btn"
        >
          {bulkRunning === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Auto-translate all missing EL
        </Button>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {SECTIONS.map(sec => {
          const missing = getMissingCount(sec.key);
          return (
            <button
              key={sec.key}
              onClick={() => setActiveSection(sec.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeSection === sec.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
              data-testid={`translations-tab-${sec.key}`}
            >
              {sec.label}
              {missing > 0 && <span className="ml-1.5 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{missing}</span>}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="bg-card rounded-xl border border-border mb-4 p-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="translations-search" />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleBulkTranslate(activeSection)}
          disabled={bulkRunning !== null || getMissingCount(activeSection) === 0}
          className="gap-2 shrink-0"
          data-testid={`bulk-translate-section-btn`}
          title={`Auto-translate missing EL fields in ${SECTIONS.find(s => s.key === activeSection)?.label}`}
        >
          {bulkRunning === activeSection ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Translate section ({getMissingCount(activeSection)})
        </Button>
      </div>

      {/* Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-48">Item</TableHead>
              <TableHead>Field</TableHead>
              <TableHead>English (EN)</TableHead>
              <TableHead>Greek (EL)</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No items found</TableCell></TableRow>
            ) : (
              filtered.map(item => (
                Object.entries(item.fields || {}).map(([field, vals], fIdx) => {
                  const key = `${item.id}_${field}`;
                  const elValue = edits[key] !== undefined ? edits[key] : (vals.el || "");
                  const isMissing = vals.en && !vals.el && !edits[key];
                  return (
                    <TableRow key={key} className={isMissing ? "bg-red-50/50" : ""} data-testid={`translation-row-${key}`}>
                      {fIdx === 0 && (
                        <TableCell rowSpan={Object.keys(item.fields).length} className="font-medium align-top border-r">
                          <div className="text-sm">{item.name}</div>
                          {item.collection && <div className="text-[10px] text-muted-foreground">{item.collection}</div>}
                        </TableCell>
                      )}
                      <TableCell className="text-xs text-muted-foreground font-mono">{field}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">{vals.en || <span className="text-muted-foreground italic">empty</span>}</TableCell>
                      <TableCell>
                        <Input
                          value={elValue}
                          onChange={e => handleEdit(item.id, field, e.target.value)}
                          className="h-8 text-sm"
                          placeholder="Greek translation..."
                          data-testid={`translation-input-${key}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleAutoTranslate(item, field)}
                            disabled={translating[key] || !vals.en}
                            className="p-1 rounded hover:bg-muted disabled:opacity-30 transition-colors"
                            title="Auto-translate"
                            data-testid={`translate-btn-${key}`}
                          >
                            {translating[key] ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> : <Languages className="h-3.5 w-3.5 text-primary" />}
                          </button>
                          <button
                            onClick={() => handleSave(item, field)}
                            disabled={saving[key] || edits[key] === undefined}
                            className="p-1 rounded hover:bg-muted disabled:opacity-30 transition-colors"
                            title="Save"
                            data-testid={`save-btn-${key}`}
                          >
                            {saving[key] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 text-green-600" />}
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
